/**
 * 数据主权区的**文案唯一来源**（逐字取自原型 `08-家庭与设置_原型.html` 面板 3）
 *
 * ## 🔴 为什么文案要单独一个文件
 *
 * 本项目一贯做法（`theme-labels.ts` / `reminder-labels.ts` / …）：文案与组件分开，
 * 让 **spec 能对"字"断言**（本项目禁用"靠 `includes('导出')` 这类恒真信号"的断言 —— ㉝ 批教训）。
 *
 * ## ⚠️ 两处**刻意偏离原型**（原型过时 · 铁律 23 的又一形态）
 *
 * | 原型原文 | 本实现 | 为什么 |
 * |---|---|---|
 * | 「覆盖全部业务表 **38 / 42**」 | 「覆盖全部业务表（除下方明示的 N 张）」 | 38/42 是 **v1 口径**；实测业务表 **36**（`DR-031`）⇒ 导出 **33**（排除 3）。🔴 端侧**拿不到** schema 表数（那是服务端的），故只写"除下方明示的 N 张"，N 由共享层 `BACKUP_EXCLUDED_TABLES.length` **算出来** |
 * | 「不在导出范围内的内容」列举 4 项（含**微信绑定标识**） | 改为**逐条列出共享层常量**（3 条） | `DR-029` 已把 `wechat_bindings` **并入 `users`** ⇒ 只剩 3 张；且"逐条明示 + 给原因"是 `AC-N4.11` 的原文要求 ⇒ 用列表比一句话更贴 AC |
 *
 * 📌 原型本身也已顺手订正（`38/42 → 33`、`4 张 → 3 张`）—— 见本批 changelog。
 */

import { DELETION_COOLING_DAYS } from '@jiaying/shared';

/** 卡片标题（原型面板 3 的 `sec-head h2`） */
export const SECTION_TITLE = '数据主权';

/** 卡片说明（原型 `sovereign-head` 的原文） */
export const CARD_NOTE = '导出不收钱、不用升级、不限次数 —— 这不是会员权益，是你的基本权利';

// ─────────────────────────── 档 1：导出流水（CSV） ───────────────────────────

export const CSV_TITLE = '导出流水（CSV）';
export const CSV_DESC = '以分录腿为粒度（一腿一行），含币种与标签，可完整还原复式结构；UTF-8，可直接用 Excel 打开';
export const CSV_ACTION = '导出 CSV';
/**
 * 🔴 设置页**没有筛选上下文** ⇒ 这里的语义是「建账以来全部流水」（走 `exportAll=true`）。
 * 「导出**当前筛选**」的宿主在**流水页工具条**（`AC-N4.4`）—— 两处入口语义互补，不重复。
 */
export const CSV_ALL_NOTE = '这里是建账以来全部流水；按当前筛选导出，在「流水」页的工具条上。';

// ─────────────────────── 档 2：导出完整备份（JSON） ───────────────────────

export const BACKUP_TITLE = '导出完整备份（JSON）';
export const BACKUP_ACTION = '导出 JSON';
export const BACKUP_FALLBACK_DESC = '含分录腿、净资产快照、标签关联与账本设置；可用于恢复或迁移到其他工具';

/**
 * 备份说明 —— 🔴 张数**算出来**（`BACKUP_EXCLUDED_TABLES.length`），不写死：
 * 将来排除清单变了（如产品决定把 `migration_watermark` 也排除），这句话自动跟着变。
 */
export function backupDesc(excludedCount: number): string {
  return `覆盖全部业务表（除下方明示的 ${String(excludedCount)} 张），${BACKUP_FALLBACK_DESC}`;
}

// ───────────────────── 不在导出范围内的内容（明示） ─────────────────────

export const EXCLUDED_TITLE = '不在导出范围内的内容（明示）';
export const EXCLUDED_NOTE = '含凭证或个人敏感信息，导出会扩大泄露面，故不纳入';

/** 右侧计数标签（原型是 `<span class="tag">4 张表</span>`；数字同样**算出来**） */
export function excludedTag(count: number): string {
  return `${String(count)} 张表`;
}

// ⚠️ 「导出中… / 导出失败：… / 已导出 xxx」三条反馈**不在这里** ——
//   它们被**两个入口共用**（本卡 + 流水页工具条）⇒ 唯一来源 = `features/export-labels.ts`。

// ───────────────────── N4 注销（「删除账户与全部数据」） ─────────────────────

export const DELETE_TITLE = '删除账户与全部数据';
export const DELETE_DESC = '不可撤销。删除前会要求你确认已导出备份，并给出冷静期';
export const DELETE_ACTION = '删除账户';

/** 对话框的四步（原型「态展示 · 注销账号」的浓缩 —— 用**共享层常量**算天数，禁写死 7） */
export const DELETION_STEP_EXPORT = '第 1 步 · 导出确认';
export const DELETION_EXPORT_LABEL = '我已导出备份（数据随时可再导一次，但删了就拿不回来了）';
export const DELETION_STEP_CONFIRM = '第 2 步 · 二次输入';
export function deletionConfirmHint(accountName: string): string {
  return `请输入你的账号名「${accountName}」以确认`;
}
export const DELETION_STEP_COOLING = `第 3 步 · 冻结 · 冷静期 ${String(DELETION_COOLING_DAYS)} 天`;
export const DELETION_COOLING_NOTE =
  '冷静期内账号会被冻结（不可登录），剩余天数双端可见，且随时可以撤销。';
export const DELETION_STEP_PURGE = '第 4 步 · 期满删除';
export const DELETION_PURGE_NOTE = '全部账本与业务数据 + 账号，不可逆。';
export const DELETION_WARN = '期满后，你的全部账本与业务数据、以及账号本身都会被真删除 —— 不可恢复。';
export const DELETION_CONFIRM = '确认删除';
export const DELETION_CANCEL = '再想想';
/** owner 仍有成员时服务端 409 的补充说明（服务端已给 message，这里是**静态**的引导语） */
export const DELETION_OWNER_BLOCKED_NOTE =
  '如果你仍是某本家庭账的所有者且还有其他成员：请先「转让所有权」或「解散账本」，再回来注销。';

// ───────────────────── N4 恢复（「从备份恢复」· 批次 `65`） ─────────────────────

/** 入口标题（原型 `08` 的 `row-title` 原文） */
export const RESTORE_TITLE = '从备份恢复';
/** 入口按钮（本卡上那个 —— 🔴 原型 `08` 的 `row-end` 原文逐字） */
export const RESTORE_ACTION = '上传备份';

/**
 * 恢复入口的一行说明（原型 `08` 的 `row-desc` 原文改写）。
 *
 * ⚠️ 原型那句原文里写着"**覆盖前自动快照 + 可回滚**尚未做（需要新表 ⇒ 待 DR 拍板）"——
 * 那是**给设计者看的备注**，不该出现在产品 UI 里（用户不需要知道内部待办）。
 * ⇒ 本行**只陈述用户能感知的事实**：先预览、冲突逐项可裁决、覆盖须先导出并二次输入。
 * 🔴 同时**不承诺**"可回滚"（`AC-N4.21` 未做）—— 承诺一件做不到的事是本页最重的违规。
 */
export const RESTORE_DESC =
  '恢复前先展示预览报告：新增 / 覆盖 / 冲突三项计数，冲突逐项可裁决；合并模式下若有丢弃行会列出清单。不会静默覆盖现有数据。覆盖模式须先完成导出并二次输入账本名。';

/** owner 之外的成员看到的那句（🆕 恢复是 owner 闸，与"导出全员可用"刻意不对称） */
export const RESTORE_OWNER_ONLY_NOTE =
  '只有账本所有者可以执行恢复（它是破坏性动作，可能清空本账本内容）；导出对所有人开放，恢复不是。';
